import type { ReactNode } from "react";
import { SlideHeader } from "../../../SlideHeader";

interface Layer {
  num: string;
  label: string;
  body: ReactNode;
  tags: string[];
}

const LAYERS: Layer[] = [
  {
    num: "01",
    label: "Intelligence in",
    body: (
      <>
        Four daily streams{" "}
        <span className="font-semibold text-slate-900">
          continuously feed the next audit
        </span>
        : store-listing snapshots, competitor metadata + rankings + assets,
        AI/web discoverability probes, ASC + Play Console performance.
      </>
    ),
    tags: [
      "4 daily streams",
      "ASC + Play ingestion",
      "ChatGPT · Claude · Gemini · Perplexity",
      "competitor metadata · ranking · assets",
    ],
  },
  {
    num: "02",
    label: "Methodology applied",
    body: (
      <>
        Ten engines apply senior-practitioner methodology across four scoring
        pillars.{" "}
        <span className="font-semibold text-slate-900">
          100+ expert prompts per audit
        </span>
        , cross-validated across multiple frontier models.
      </>
    ),
    tags: [
      "10 specialized engines",
      "4 scoring pillars",
      "100+ prompts / audit",
      "multi-provider cross-validation",
      "weekly methodology updates",
    ],
  },
  {
    num: "03",
    label: "Audit out",
    body: (
      <>
        The audit artifact:{" "}
        <span className="font-semibold text-slate-900">
          ranked recommendations
        </span>{" "}
        with plain-language reasoning, per-pillar scores, methodology lineage,
        and the customer's own ASC + Play numbers in context.
      </>
    ),
    tags: [
      "ranked recommendations",
      "plain-language reasoning",
      "per-pillar scores",
      "methodology lineage",
      "customer's-own-data context",
    ],
  },
  {
    num: "04",
    label: "Feedback compounds",
    body: (
      <>
        Every shipped recommendation is measured on the customer's own ASC +
        Play performance — score history, impact attribution, and sentiment
        feed the{" "}
        <span className="font-semibold text-slate-900">
          next audit's calibration
        </span>
        .
      </>
    ),
    tags: [
      "per-engine score history",
      "recommendation impact attribution",
      "sentiment feedback",
      "calibrates next audit",
    ],
  },
];

const FABRIC: string[] = [
  "Cloudflare Workers (Hono)",
  "Firebase Functions",
  "Encrypted Worker→Functions proxy",
  "Multi-provider AI gateway (OpenAI · Anthropic · Google · Perplexity)",
  "KMS-encrypted credential vault",
  "Geo-distributed proxy fleet",
  "Multi-layer caching (10–50× inference cost compression)",
  "GCS audit outputs",
  "BigQuery analytics",
  "Daily change detection",
  "Cost-aware orchestration · per-engine cancellation",
  "Snapshot / invariant testing",
];

export default function PlatformDepth() {
  return (
    <div className="flex w-full flex-col px-6 pt-8 pb-4 text-slate-900 md:h-full md:pl-24 md:pr-20 md:pt-16 md:pb-10 md:[&_header]:mb-6">
      <SlideHeader unnumbered eyebrow="Appendix B · Platform Depth">
        <h1 className="text-2xl font-semibold leading-tight tracking-tight md:text-4xl md:leading-[1.15]">
          How each audit is produced.
        </h1>
      </SlideHeader>

      <div className="flex min-h-0 flex-col gap-2.5 md:flex-1">
        {LAYERS.map((layer) => (
          <LayerRow key={layer.num} {...layer} />
        ))}
        <FabricStrip items={FABRIC} />
      </div>
    </div>
  );
}

function LayerRow({ num, label, body, tags }: Layer) {
  return (
    <div className="rounded-xl bg-white p-3 ring-1 ring-slate-200 md:p-3.5">
      <div className="flex items-start gap-3 md:gap-4">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-600 text-sm font-bold text-white md:h-9 md:w-9 md:text-base">
          {num}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-bold uppercase tracking-[0.14em] text-blue-700 md:text-sm md:tracking-[0.16em]">
            {label}
          </p>
          <p className="mt-1.5 text-sm leading-snug text-slate-700 md:text-[15px]">
            {body}
          </p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {tags.map((tag) => (
              <span
                key={tag}
                className="rounded-full bg-slate-100 px-2.5 py-0.5 text-[10px] font-medium text-slate-600 ring-1 ring-slate-200 md:text-[11px]"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function FabricStrip({ items }: { items: string[] }) {
  return (
    <div className="rounded-xl bg-slate-50 p-3 ring-1 ring-slate-200 md:p-3.5">
      <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 md:text-xs">
        Production fabric
      </p>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {items.map((item) => (
          <span
            key={item}
            className="rounded-full bg-white px-2.5 py-0.5 text-[10px] font-medium text-slate-700 ring-1 ring-slate-200 md:text-[11px]"
          >
            {item}
          </span>
        ))}
      </div>
    </div>
  );
}
